import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSessionHeaders } from '../../lib/serverSession';
import { AnnouncementLayout, UpdateBlock } from './AnnouncementLayout';

const ANNOUNCEMENT_URL = '/api/user/announcements';

type Announcement = {
  id: string;
  title: string;
  body: string;
  blocks?: UpdateBlock[];
  published_at?: string;
};

export const AnnouncementPopup: React.FC<{ audience?: 'shareholder' | 'affiliate' }> = ({ audience = 'shareholder' }) => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [understood, setUnderstood] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const headers = getSessionHeaders();
      if (!headers['X-Session-Id']) return;
      try {
        const response = await fetch(ANNOUNCEMENT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ operation: 'current', audience }),
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || data?.impersonated || !data?.announcement?.id) return;
        setAnnouncement(data.announcement);
      } catch {
        // Leave the dashboard usable. The notice is loaded again on the next sign-in.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [audience]);

  useEffect(() => {
    if (!announcement) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [announcement]);

  const acknowledge = async () => {
    if (!announcement || saving || !understood) return;
    setSaving(true);
    setSaveError('');
    try {
      const headers = getSessionHeaders();
      const response = await fetch(ANNOUNCEMENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({
            operation: 'acknowledge',
            announcementId: announcement.id,
            audience,
            understood: true,
          }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        setSaveError(data?.error || 'We could not save your acknowledgement. Please try again.');
        return;
      }
      setAnnouncement(null);
    } catch {
      setSaveError('We could not save your acknowledgement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!announcement || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(0, 0, 0, 0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
        style={{
          width: 'min(640px, 100%)',
          maxHeight: 'min(88vh, 820px)',
          display: 'flex',
          flexDirection: 'column',
          background: '#111827',
          color: '#f9fafb',
          border: '1px solid rgba(251, 191, 36, 0.45)',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
        }}
      >
        <div style={{ overflow: 'auto', minHeight: 0, flex: '1 1 auto', padding: '24px 24px 12px' }}>
          <p style={{ margin: '0 0 8px', color: '#d4af37', fontSize: '0.8rem', letterSpacing: '0.04em' }}>
            ANNOUNCEMENT
          </p>
          <h2 id="announcement-title" style={{ margin: '0 0 12px', fontSize: '1.35rem', lineHeight: 1.3, color: '#fbbf24' }}>
            {announcement.title}
          </h2>
          <AnnouncementLayout blocks={announcement.blocks} fallback={announcement.body} />
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', padding: '14px 24px 18px' }}>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '12px', color: '#e5e7eb', lineHeight: 1.4 }}>
            <input
              type="checkbox"
              checked={understood}
              onChange={(event) => setUnderstood(event.target.checked)}
              style={{ marginTop: '3px' }}
            />
            <span>I have read and understand this update.</span>
          </label>
          {saveError ? (
            <p style={{ margin: '0 0 10px', color: '#fecaca', fontSize: '0.9rem' }}>{saveError}</p>
          ) : null}
          <button
            type="button"
            onClick={acknowledge}
            disabled={saving || !understood}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 16px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: saving || !understood ? 'not-allowed' : 'pointer',
              background: saving || !understood ? '#4b5563' : '#fbbf24',
              color: '#111827',
            }}
          >
            {saving ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default AnnouncementPopup;
